import DataGridComponent from "components/DataGridComponent/DataGridComponent"
import { DeleteDialog } from "components/DeleteDialog/DeleteDialog"
import { CriteriaCatalog, Norm } from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { useEffect, useState } from "react"
import { getNormName } from "utils/getNormName"
import { AddCriteriaCatalog } from "./AddCriteriaCatalog"

export const criteriaCatalogEmpty: CriteriaCatalog = {
  id: "NEW",
  name: "",
  norm: [],
  examinationConfig: [],
  chapterNeeded: false,
  criteriaList: [],
  description: [],
}

const AdminCriteriaCatalogs = () => {
  const { criteriaCatalogs, examinationConfigs, norms } = useStoreState((state) => state)
  const {
    updateCriteriaCatalog,
    deleteCriteriaCatalog,
    fetchCriteriaCatalogs,
    fetchExaminationConfigs,
  } = useStoreActions((actions) => actions)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [formState, setFormState] = useState<CriteriaCatalog>(criteriaCatalogEmpty)
  const [openDelete, setOpenDelete] = useState(false)
  const [toDelete, setToDelete] = useState<string[]>([])

  useEffect(() => {
    const loadCriteriaCatalogs = async () => {
      try {
        await fetchCriteriaCatalogs({ fresh: false })
      } catch (err) {
        console.error("Error loading criteria catalogs:", err)
      }
    }
    loadCriteriaCatalogs()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    fetchExaminationConfigs()
  }, [fetchExaminationConfigs])

  const handleEdit = (id: string) => {
    if (id === "NEW") {
      setFormState({
        ...criteriaCatalogEmpty,
        id: "NEW",
      })
    } else {
      const criteriaCatalog = criteriaCatalogs.find((tb) => tb.id === id)
      if (criteriaCatalog) {
        setFormState(criteriaCatalog)
      }
    }
    setDialogOpen(true)
  }

  const handleDelete = (id: string[]) => {
    setOpenDelete(true)
    setToDelete(id)
  }

  const handleDeleteSelected = () => {
    deleteCriteriaCatalog(toDelete)
    setToDelete([])
    setOpenDelete(false)
  }

  const handleDialogClose = () => {
    setDialogOpen(false)
  }

  const handleFormSubmit = (arg: CriteriaCatalog) => {
    updateCriteriaCatalog(arg)
    setDialogOpen(false)
  }

  const getCopyBaseName = (name: string) => {
    const trimmed = name.trim()
    const withoutSuffix = trimmed.replace(/\s*\(\d+\)$/u, "").trim()
    if (withoutSuffix.length > 0) {
      return withoutSuffix
    }
    if (trimmed.length > 0) {
      return trimmed
    }
    return "Pass-Fail-Criteria"
  }

  const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

  const getNextCopyName = (name: string) => {
    const baseName = getCopyBaseName(name)
    const pattern = new RegExp(`^${escapeRegExp(baseName)}(?: \\((\\d+)\\))?$`)
    let highestNumber = 0

    criteriaCatalogs.forEach((block) => {
      const match = block.name.match(pattern)
      if (match) {
        const suffix = match[1] ? parseInt(match[1], 10) : 0
        if (!Number.isNaN(suffix) && suffix > highestNumber) {
          highestNumber = suffix
        }
      }
    })

    const nextNumber = highestNumber + 1
    return `${baseName} (${nextNumber})`
  }

  const handleCopy = (id: string) => {
    const criteriaCatalog = criteriaCatalogs.find((tb) => tb.id === id)
    if (!criteriaCatalog) {
      return
    }

    const copiedCriteriaCatalog: CriteriaCatalog = {
      ...criteriaCatalog,
      id: "NEW",
      name: getNextCopyName(criteriaCatalog.name),
    }

    updateCriteriaCatalog(copiedCriteriaCatalog)
  }

  const columns = [
    { field: "name", headerName: "Bezeichnung", width: 300 },
    {
      field: "examinationConfig",
      headerName: "Examination",
      flex: 200,
      valueGetter: (params: string[]) =>
        params.map((id) => examinationConfigs.find((e) => e.id === id)?.name).join(", "),
    },
    {
      field: "norm",
      headerName: "Norms",
      flex: 200,
      valueGetter: (params: string[]) => {
        const returnValue = params
          .map((id) => norms.find((n) => n.id === id))
          .filter((n) => n !== undefined) as Norm[]
        return returnValue.map((n) => getNormName(n)).join(", ")
      },
    },
  ]

  return (
    <>
      <DataGridComponent
        columns={columns}
        rows={criteriaCatalogs}
        getRowId={(row) => row.id}
        onEdit={handleEdit}
        onAdd={() => handleEdit("NEW")}
        onDelete={handleDelete}
        onCopy={handleCopy}
      />

      <AddCriteriaCatalog
        open={dialogOpen}
        onClose={handleDialogClose}
        formState={formState}
        onSubmit={handleFormSubmit}
      />

      <DeleteDialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        onDelete={handleDeleteSelected}
      />
    </>
  )
}

export default AdminCriteriaCatalogs
